import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, Target, MapPin, Briefcase, DollarSign, ArrowRight, CheckCircle2, XCircle, RefreshCw, Lightbulb,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";

/**
 * iter-171 — Talent-side Fit Preview.
 *
 * Shows the talent "how I match today" against open roles using the same
 * factor engine that drives the employer Browse view. No scores, no stars.
 * Mirrors spec §5 exactly on the talent side.
 */
export const TalentFitPreview = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/professional/fit-preview", {
                params: { limit: 5 },
            });
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-6">
                <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            </div>
        );
    }
    if (!data) return null;

    return (
        <section data-testid="talent-fit-preview" className="rounded-xl border border-border bg-card p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Target size={14} /> <p className="overline">Your fit on open roles</p>
                    </div>
                    <h2 className="mt-1 font-heading font-semibold text-lg text-foreground">
                        Here&apos;s how your Passport reads today
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                        Focus on the missing items to show up in more employer searches. No scores — just the factor breakdown you can act on.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="fit-refresh"
                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                >
                    <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {!data.has_passport ? (
                <div className="mt-5 rounded-lg border border-border bg-background p-6 text-center" data-testid="fit-no-passport">
                    <p className="text-sm text-foreground font-medium">Build your Talent Passport to see fit on open roles.</p>
                    <Link to="/professional/passport"
                          data-testid="fit-cta-passport"
                          className="mt-3 inline-flex items-center gap-1 btn-green text-xs px-3 py-1.5">
                        Open Talent Passport <ArrowRight size={11} />
                    </Link>
                </div>
            ) : data.roles.length === 0 ? (
                <div className="mt-5 rounded-lg border border-border bg-background p-6 text-center" data-testid="fit-empty">
                    <p className="text-sm text-foreground font-medium">
                        No matching open roles right now.
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Add skills to your Passport to broaden what you match against.
                    </p>
                </div>
            ) : (
                <>
                    {data.top_missing?.length > 0 && (
                        <div data-testid="fit-top-missing" className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs flex items-start gap-2">
                            <Lightbulb size={13} className="shrink-0 mt-0.5 text-amber-600" />
                            <div className="flex-1">
                                <div className="font-semibold text-amber-900 dark:text-amber-300">Add these to unlock more roles</div>
                                <div className="mt-0.5 text-amber-800 dark:text-amber-200">
                                    {data.top_missing.map((m, i) => (
                                        <span key={m.label}>
                                            {i > 0 && " · "}
                                            <span className="font-medium">{m.label}</span>
                                            <span className="opacity-70"> ({m.roles})</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <ul className="mt-4 space-y-3" data-testid="fit-roles-list">
                        {data.roles.map(({ role, factors, match_summary, missing_summary }) => (
                            <li key={role.id} data-testid={`fit-role-${role.id}`}
                                className="rounded-lg border border-border bg-background p-3">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-foreground truncate">{role.title}</div>
                                        <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                            {role.company && <span>{role.company}</span>}
                                            {(role.city || role.state) && (
                                                <span className="inline-flex items-center gap-0.5"><MapPin size={10} />{role.city}{role.city && role.state ? ", " : ""}{role.state}</span>
                                            )}
                                            {role.remote_status && (
                                                <span className="inline-flex items-center gap-0.5"><Briefcase size={10} />{role.remote_status.replace("_", " ")}</span>
                                            )}
                                            {role.salary_min && (
                                                <span className="inline-flex items-center gap-0.5"><DollarSign size={10} />${(role.salary_min / 1000).toFixed(0)}k{role.salary_max ? `–${(role.salary_max / 1000).toFixed(0)}k` : "+"}</span>
                                            )}
                                        </div>
                                        <div className="mt-2 text-xs">
                                            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                                                {match_summary}
                                            </Badge>
                                            {missing_summary && (
                                                <span className="ml-2 text-[11px] text-muted-foreground">{missing_summary}</span>
                                            )}
                                        </div>
                                        <details className="mt-2">
                                            <summary data-testid={`fit-expand-${role.id}`} className="text-[11px] text-emerald-700 dark:text-emerald-400 cursor-pointer select-none">
                                                View factor breakdown
                                            </summary>
                                            <ul className="mt-2 space-y-1">
                                                {factors.map((f) => (
                                                    <li key={f.key} data-testid={`fit-factor-${role.id}-${f.key}`} className="flex items-center gap-1.5 text-[11px]">
                                                        {f.met
                                                            ? <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />
                                                            : <XCircle size={11} className="text-rose-500 shrink-0" />}
                                                        <span className={f.met ? "text-foreground" : "text-muted-foreground"}>{f.label}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    </div>
                                    <Link
                                        to={role.slug ? `/jobs/${role.slug}` : `/jobs?job=${role.id}`}
                                        data-testid={`fit-view-${role.id}`}
                                        className="shrink-0 inline-flex items-center gap-1 btn-outline text-xs px-3 py-1.5"
                                    >
                                        View role <ArrowRight size={11} />
                                    </Link>
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </section>
    );
};

export default TalentFitPreview;
